/**
 * CaseReadinessPanel - Document Readiness Status Display
 * ========================================================
 * 
 * Shows whether a case is ready for accountant review with clear blockers.
 * Used in wizard sidebar and case detail pages.
 * 
 * P0: Document Readiness System - Frontend Component
 * @author ClaimSync Team
 * @created August 2025
 */

import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  FileText,
  Building2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Upload,
  FileSpreadsheet
} from 'lucide-react';

interface ReadinessCheck {
  name: string;
  status: 'passed' | 'failed' | 'pending' | 'not_required';
  required: boolean;
  message: string;
  details?: Record<string, any>;
}

interface ReadinessResult {
  case_id: string;
  is_ready: boolean;
  status: 'ready' | 'not_ready' | 'blocked';
  overall_message: string;
  checks: ReadinessCheck[];
  blocking_issues: string[];
  warnings: string[];
  entity_type: string | null;
  assessed_at: string;
}

interface CaseReadinessPanelProps {
  caseId: string;
  /** Compact mode for sidebar display */
  compact?: boolean;
  /** Callback when readiness status changes */
  onStatusChange?: (isReady: boolean) => void;
  /** Show refresh button */
  showRefresh?: boolean;
  /** Custom class name */
  className?: string;
}

const STATUS_CONFIG = {
  passed: {
    icon: CheckCircle,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200'
  },
  failed: {
    icon: XCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200'
  },
  pending: {
    icon: Clock,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200'
  },
  not_required: {
    icon: CheckCircle,
    color: 'text-slate-400',
    bg: 'bg-slate-50',
    border: 'border-slate-200'
  }
};

const CHECK_ICONS: Record<string, typeof FileText> = {
  'Bank Statements': FileSpreadsheet,
  'Tax Returns': FileText,
  'K-1 Forms': FileText,
  'P&L Statement': FileSpreadsheet,
  'Transaction Review': Building2,
  'Clarifications': AlertTriangle
};

export default function CaseReadinessPanel({
  caseId,
  compact = false,
  onStatusChange,
  showRefresh = true,
  className = ''
}: CaseReadinessPanelProps) {
  const [readiness, setReadiness] = useState<ReadinessResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!compact);

  const fetchReadiness = async () => {
    if (!caseId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await api.get<ReadinessResult>(`/api/v1/queue/case/${caseId}/readiness`);
      setReadiness(data);
      onStatusChange?.(data.is_ready);
    } catch (err: any) {
      console.error('Error fetching readiness:', err);
      setError(err.message || 'Failed to check readiness');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReadiness();
  }, [caseId]);

  if (loading) {
    return (
      <div className={`p-4 rounded-lg border border-slate-200 bg-white ${className}`}>
        <div className="flex items-center gap-2 text-slate-500">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm">Checking readiness...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 rounded-lg border border-red-200 bg-red-50 ${className}`}>
        <div className="flex items-center gap-2 text-red-600">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">{error}</span>
        </div>
        {showRefresh && (
          <button
            onClick={fetchReadiness}
            className="mt-2 text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" />
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!readiness) return null;

  const overallStatus = readiness.is_ready ? 'passed' : 'failed';
  const OverallIcon = STATUS_CONFIG[overallStatus].icon;

  // Compact view for sidebar
  if (compact && !expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className={`w-full p-3 rounded-lg border ${STATUS_CONFIG[overallStatus].border} ${STATUS_CONFIG[overallStatus].bg} text-left transition-colors hover:opacity-80 ${className}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <OverallIcon className={`w-4 h-4 ${STATUS_CONFIG[overallStatus].color}`} />
            <span className={`text-sm font-medium ${STATUS_CONFIG[overallStatus].color}`}>
              {readiness.is_ready ? 'Ready for Review' : 'Not Ready'}
            </span>
          </div>
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </div>
        {!readiness.is_ready && readiness.blocking_issues.length > 0 && (
          <p className="text-xs text-slate-600 mt-1 truncate">
            {readiness.blocking_issues[0]}
          </p>
        )}
      </button>
    );
  }

  return (
    <div className={`rounded-lg border ${STATUS_CONFIG[overallStatus].border} ${STATUS_CONFIG[overallStatus].bg} overflow-hidden ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-inherit">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${readiness.is_ready ? 'bg-emerald-100' : 'bg-red-100'}`}>
              <OverallIcon className={`w-5 h-5 ${STATUS_CONFIG[overallStatus].color}`} />
            </div>
            <div>
              <h3 className={`font-semibold ${STATUS_CONFIG[overallStatus].color}`}>
                {readiness.is_ready ? 'Ready for Accountant Review' : 'Not Ready for Review'}
              </h3>
              <p className="text-sm text-slate-600">
                {readiness.overall_message}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {showRefresh && (
              <button
                onClick={fetchReadiness}
                disabled={loading}
                className="p-2 text-slate-500 hover:text-slate-700 hover:bg-white/50 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            {compact && (
              <button
                onClick={() => setExpanded(false)}
                className="p-2 text-slate-500 hover:text-slate-700 hover:bg-white/50 rounded-lg transition-colors"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Blocking Issues */}
      {readiness.blocking_issues.length > 0 && (
        <div className="p-4 bg-red-50 border-b border-red-200">
          <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            Blocking Issues ({readiness.blocking_issues.length})
          </h4>
          <ul className="space-y-1">
            {readiness.blocking_issues.map((issue, i) => (
              <li key={i} className="text-sm text-red-600 flex items-start gap-2">
                <span className="text-red-400 mt-1">•</span>
                {issue}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Checks List */}
      <div className="p-4">
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
          Document Checklist
        </h4>
        <div className="space-y-2">
          {readiness.checks.map((check, i) => {
            const config = STATUS_CONFIG[check.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.pending;
            const CheckIcon = CHECK_ICONS[check.name] || FileText;
            const StatusIcon = config.icon;

            return (
              <div
                key={i}
                className={`flex items-center justify-between p-3 rounded-lg border ${config.border} ${config.bg}`}
              >
                <div className="flex items-center gap-3">
                  <CheckIcon className={`w-4 h-4 ${config.color}`} />
                  <div>
                    <span className="text-sm font-medium text-slate-900">
                      {check.name}
                    </span>
                    {!check.required && (
                      <span className="ml-2 text-xs text-slate-400">(optional)</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${config.color}`}>
                    {check.message.length > 40 ? check.message.substring(0, 40) + '...' : check.message}
                  </span>
                  <StatusIcon className={`w-4 h-4 ${config.color}`} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Entity Type */}
      {readiness.entity_type && readiness.entity_type !== 'unknown' && (
        <div className="px-4 pb-4">
          <div className="text-xs text-slate-500">
            Entity Type: <span className="font-medium text-slate-700 capitalize">{readiness.entity_type.replace('_', ' ')}</span>
          </div>
        </div>
      )}

      {/* Action Button for Upload */}
      {!readiness.is_ready && (
        <div className="p-4 bg-white border-t border-slate-200">
          <a
            href={`/wizard-v2?draft=${caseId}&step=3&section=upload`}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <Upload className="w-4 h-4" />
            Upload Missing Documents
          </a>
        </div>
      )}
    </div>
  );
}
